/**
 * LocationPicker.jsx — searchable location combobox.
 *
 * A plain text field was wrong here for two reasons. Free text means "Islington",
 * "islington", "Islington, London" and "N1" all become different values, so the
 * briefing function cannot reliably match a location to its demographic
 * context. And a user has no way of knowing the app understands boroughs at all.
 *
 * Built to the ARIA combobox pattern rather than a native <select>, because the
 * list is ~250 entries and needs type-ahead. That means the keyboard support is
 * hand-rolled and worth keeping: arrows move, Enter selects, Escape closes,
 * Tab commits, and aria-activedescendant tells a screen reader which option is
 * highlighted without moving real focus out of the input.
 *
 * The demographic note under each option is doing real work — it shows the user
 * what the app will actually do with their answer, which is the disclosure that
 * makes asking for location feel reasonable rather than nosy.
 */

import { useState, useRef, useEffect, useMemo, useId, useCallback } from 'react';
import { MapPin, Search, X } from 'lucide-react';
import { searchLocations, locationLabel, findLocation } from '../lib/locations';

export default function LocationPicker({ value, onChange, label = 'City, borough or region', hint }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selected, setSelected] = useState(() => findLocation(value));

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const results = useMemo(() => searchLocations(query), [query]);

  // Re-sync if the parent changes the value (e.g. loading an existing profile).
  useEffect(() => {
    const found = findLocation(value);
    if (found) setSelected(found);
    else if (!value) setSelected(null);
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const choose = useCallback((loc) => {
    setSelected(loc);
    onChange(locationLabel(loc), loc);
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, [onChange]);

  const clear = useCallback(() => {
    setSelected(null);
    onChange('', null);
    setQuery('');
    inputRef.current?.focus();
  }, [onChange]);

  const onKeyDown = (e) => {
    if (!open && ['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
      setOpen(true);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % Math.max(results.length, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
        break;
      case 'Enter':
        if (activeIndex >= 0 && results[activeIndex]) {
          e.preventDefault();
          choose(results[activeIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setQuery('');
        setActiveIndex(-1);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  // Group consecutive results by type so the list reads as sections.
  const grouped = useMemo(() => {
    const out = [];
    let lastType = null;
    results.forEach((loc, i) => {
      if (loc.type !== lastType) {
        out.push({ heading: loc.type });
        lastType = loc.type;
      }
      out.push({ loc, index: i });
    });
    return out;
  }, [results]);

  return (
    <div className="field locpicker" ref={wrapRef}>
      <label htmlFor={`${id}-input`} className="field__label">{label}</label>

      {selected ? (
        <div className="locpicker__chosen">
          <MapPin size={16} aria-hidden="true" />
          <div className="locpicker__chosen-text">
            <strong>{locationLabel(selected)}</strong>
            {selected.context && <span>{selected.context}</span>}
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={clear}
            aria-label={`Change location, currently ${locationLabel(selected)}`}
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="locpicker__control">
          <Search size={16} aria-hidden="true" />
          <input
            id={`${id}-input`}
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? `${id}-opt-${activeIndex}` : undefined}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Start typing — Islington, Manchester, Cornwall…"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      )}

      {hint && !selected && <p className="field__hint">{hint}</p>}

      {open && !selected && (
        <div className="locpicker__panel">
          <ul
            id={`${id}-list`}
            ref={listRef}
            role="listbox"
            aria-label="Locations"
            className="locpicker__list"
          >
            {results.length === 0 && (
              <li className="locpicker__empty">
                No match for &ldquo;{query}&rdquo;. Try a nearby town, your borough,
                or your county.
              </li>
            )}

            {grouped.map((row, i) =>
              row.heading ? (
                <li key={`h-${i}`} className="locpicker__heading" role="presentation">
                  {row.heading}
                </li>
              ) : (
                <li
                  key={`${row.loc.name}-${row.loc.region}`}
                  id={`${id}-opt-${row.index}`}
                  data-index={row.index}
                  role="option"
                  aria-selected={activeIndex === row.index}
                  className={`locpicker__option${activeIndex === row.index ? ' is-active' : ''}`}
                  onMouseEnter={() => setActiveIndex(row.index)}
                  onPointerDown={(e) => { e.preventDefault(); choose(row.loc); }}
                >
                  <div className="locpicker__option-main">
                    <span className="locpicker__name">{row.loc.name}</span>
                    <span className="locpicker__region">
                      {row.loc.country === 'United Kingdom' ? row.loc.region : row.loc.country}
                    </span>
                  </div>
                  {row.loc.context && (
                    <span className="locpicker__context">{row.loc.context}</span>
                  )}
                </li>
              )
            )}
          </ul>

          <p className="locpicker__foot">
            UK locations go down to borough and town level so your briefing can be
            weighted to your actual population.
          </p>
        </div>
      )}
    </div>
  );
}

export const LOCATION_PICKER_CSS = `
.locpicker { position: relative; }

.locpicker__control {
  display: flex; align-items: center; gap: 9px;
  padding: 0 13px;
  border: 1px solid var(--line-ui); border-radius: var(--radius);
  background: var(--card);
}
.locpicker__control:focus-within { border-color: var(--primary); }
.locpicker__control svg { color: var(--ink-faint); flex-shrink: 0; }
.locpicker__control input {
  flex: 1; min-width: 0; min-height: var(--tap);
  border: none; background: none; outline: none;
  color: var(--ink); font: inherit; font-size: var(--text-base);
}

.locpicker__chosen {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 11px 11px 11px 13px;
  border: 1px solid var(--primary); border-radius: var(--radius);
  background: var(--primary-tint);
}
.locpicker__chosen > svg { color: var(--primary-text); flex-shrink: 0; margin-top: 2px; }
.locpicker__chosen-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.locpicker__chosen-text strong { font-size: var(--text-base); }
.locpicker__chosen-text span {
  font-size: var(--text-xs); color: var(--ink-soft); line-height: 1.5;
}

.locpicker__panel {
  position: absolute;
  top: 100%; left: 0; right: 0;
  z-index: var(--z-dropdown);
  margin-top: 4px;
  background: var(--card);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.locpicker__list {
  list-style: none; margin: 0; padding: 4px;
  max-height: 300px; overflow-y: auto;
  overscroll-behavior: contain;
}

.locpicker__heading {
  padding: 8px 10px 4px;
  font-family: var(--font-mono); font-size: 10px;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--ink-faint);
}

.locpicker__option {
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
}
.locpicker__option.is-active { background: var(--bg-alt); }
.locpicker__option-main {
  display: flex; align-items: baseline; gap: 8px; justify-content: space-between;
}
.locpicker__name { font-size: var(--text-sm); font-weight: 600; }
.locpicker__region { font-size: var(--text-xs); color: var(--ink-faint); flex-shrink: 0; }
.locpicker__context {
  display: block; margin-top: 2px;
  font-size: var(--text-xs); color: var(--ink-faint); line-height: 1.45;
}

.locpicker__empty {
  padding: 16px 12px; font-size: var(--text-sm);
  color: var(--ink-faint); line-height: 1.55;
}

.locpicker__foot {
  margin: 0; padding: 9px 12px;
  border-top: 1px solid var(--line); background: var(--bg-alt);
  font-size: var(--text-xs); color: var(--ink-faint); line-height: 1.45;
}
`;
